import {
  BadgeCheck,
  Boxes,
  ChartNoAxesCombined,
  Dumbbell,
  Headset,
  Megaphone,
  MessagesSquare,
  RefreshCw,
} from "lucide-react";

import { BaseLottieAnimation } from "@/components/pieces/animations/BaseLottieAnimation";
import { Container } from "@/components/pieces/bootstrap";
import {
  StyledSmallText,
  StyledSubtitle,
  StyledTitle,
} from "@/components/pieces/marketing/Titles";
import { PUBLIC_CONTENT_MAX_WIDTH } from "@/css/constants";

const SERVICES = [
  {
    icon: <Dumbbell size={36} strokeWidth={1} />,
    title: "Sales Training",
    description:
      "Boost win rates with targeted role-playing, tailored to your sales methodology.",
    bgColor: "bg-teal-200",
    iconColor: "text-teal-600",
  },
  {
    icon: <BadgeCheck size={36} strokeWidth={1} />,
    title: "Manager Effectiveness",
    description:
      "Certify key management skills to drive employee engagement and retention.",
    bgColor: "bg-yellow-100",
    iconColor: "text-yellow-600",
  },
  {
    icon: <Megaphone size={36} strokeWidth={1} />,
    title: "Leadership Development",
    description:
      "Develop leadership capabilities that drive organizational performance and team engagement.",
    bgColor: "bg-purple-100",
    iconColor: "text-purple-700",
  },
  {
    icon: <Headset size={36} strokeWidth={1} />,
    title: "Customer Service Training",
    description:
      "Improve customer satisfaction and retention through consistent service excellence.",
    bgColor: "bg-blue-200",
    iconColor: "text-blue-700",
  },
  {
    icon: <Boxes size={36} strokeWidth={1} />,
    title: "Employee Onboarding",
    description:
      "Cut new hire ramp time in half and increase early productivity during the critical first 90 days.",
    bgColor: "bg-purple-100",
    iconColor: "text-purple-700",
  },
  {
    icon: <RefreshCw size={36} strokeWidth={1} />,
    title: "Change Management",
    description:
      "Enable leaders to manage critical transitions successfully while maintaining team alignment.",
    bgColor: "bg-teal-200",
    iconColor: "text-teal-600",
  },
  {
    icon: <MessagesSquare size={36} strokeWidth={1} />,
    title: "Interview Training",
    description:
      "Help hiring managers to conduct better behavioral interviews and handle candidate questions.",
    bgColor: "bg-blue-200",
    iconColor: "text-blue-700",
  },
  {
    icon: <ChartNoAxesCombined size={36} strokeWidth={1} />,
    title: "Performance Management",
    description:
      "Equip managers to set expectations, give feedback and provide coaching to drive performance.",
    bgColor: "bg-yellow-100",
    iconColor: "text-yellow-600",
  },
];

interface ExecUseCasesProps {
  title?: string;
  smallText?: string;
  subtitle?: string;
}

export function ExecUseCases({
  title = "Exec works across the whole organization",
  smallText = "",
  subtitle = "Enterprise-grade coordination with startup-speed deployment",
}: ExecUseCasesProps) {
  return (
    <div className="py-20">
      <Container maxWidth={PUBLIC_CONTENT_MAX_WIDTH}>
        <div className="text-center">
          {smallText && <StyledSmallText>{smallText}</StyledSmallText>}
          <StyledTitle>{title}</StyledTitle>
          <StyledSubtitle>{subtitle}</StyledSubtitle>
        </div>
        <div className="mt-12">
          <BaseLottieAnimation
            animationUrl="https://placement-pub.s3.us-east-2.amazonaws.com/use-cases.json"
            className="w-full"
          />
        </div>
        <div className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
          {SERVICES.map((service, index) => (
            <div key={index} className="flex space-y-5 space-x-5 md:flex-col">
              <div
                className={`${service.bgColor} flex h-12 w-12 items-center justify-center rounded-lg`}
              >
                <span className={service.iconColor}>{service.icon}</span>
              </div>
              <div className="flex flex-col space-y-0 md:space-y-3">
                <div className="text-lg font-semibold text-gray-900">
                  {service.title}
                </div>
                <p className="text-gray-700">{service.description}</p>
              </div>
            </div>
          ))}
        </div>
      </Container>
    </div>
  );
}
